import {pattern} from 'svelte-forms/validators';
import type {Validator} from 'svelte-forms';
import {validateMaxDate, validateMinDate} from '../editors/_shared/date-time-utils';
import {isNullOrUndefined} from 'shared/utils/custom-utils';

export function requiredValidator(): Validator {
    return (value: unknown) => {
        let valid = true;
        if (isNullOrUndefined(value)) {
            valid = false;
        }
        if (typeof value === 'string') {
            const trimmed = value.replace(/\s/g, '');
            valid = trimmed.length > 0;
        }
        if (Array.isArray(value)) {
            valid = value.length > 0
        }
        return { valid, name: 'required' };
    };
}

export function patternValidator(regExp: RegExp): Validator {
    const originalValidator = pattern(regExp);
    return (value: string) => {
        if (!value) {
            return {valid: true, name: 'pattern'}
        }
        return originalValidator(value);
    };

}

export function maxLengthValidator(maxLength: number): Validator {
    return (value: string) => ({ valid: !value || value.length <= maxLength, name: 'maxlength' });
}

export function minLengthValidator(minLength: number): Validator {
    return (value: string) => ({ valid: !value || value.length >= minLength, name: 'minlength' });
}

export function minValidator(minValue: number): Validator {
    return (value: number) => ({ valid: isNullOrUndefined(value) || value >= minValue, name: 'min' });
}

export function maxValidator(maxValue: number): Validator {
    return (value: number) => ({ valid: isNullOrUndefined(value) || value <= maxValue, name: 'max' });
}

export function stepValidator(stepValue: number, minValue = 0) {
    return (value: number) => ({ valid: !value || isValidForStep(value, minValue, stepValue), name: 'step' });
}

export function notBlankValidator() {
    return (value: string) => ({ valid: !value || value.trim() !== '', name: 'notBlank' });
}

export function emailValidator() {
    // implementation copied with modification from svelte-forms, because validator name needs to be 'email' and empty value should be valid
    const regex = /^[a-zA-Z0-9_+&*-]+(?:\.[a-zA-Z0-9_+&*-]+)*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,7}$/;
    return (value: string) => ({ valid: !value || regex.test(value), name: 'email' });
}

export function urlValidator() {
    // implementation copied with modification from svelte-forms because empty value should be valid
    const regex = /(https?|ftp|git|svn):\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-z]{2,63}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/i;
    return (value: string) => ({ valid: !value || regex.test(value), name: 'url' });
}

export function maxYearValidator(maxDateInISO: string, customModelFormat: string = null): Validator {
    return (value: number) => ({ valid: !value || validateMaxDate(value.toString(10), maxDateInISO, customModelFormat), name: 'maxDate' });
}

export function minYearValidator(minDateInISO: string, customModelFormat: string = null): Validator {
    return (value: number) => ({ valid: !value || validateMinDate(value.toString(10), minDateInISO, customModelFormat), name: 'minDate' });
}

export function maxDateValidator(maxDateInISO: string, customModelFormat: string = null): Validator {
    return (value: string) => ({ valid: !value || validateMaxDate(value, maxDateInISO, customModelFormat), name: 'maxDate' });
}

export function minDateValidator(minDateInISO: string, customModelFormat: string = null): Validator {
    return (value: string) => ({ valid: !value || validateMinDate(value, minDateInISO, customModelFormat), name: 'minDate' });
}


// stolen from angularjs codebase (with minor changes)
function isValidForStep(value: number, stepBase: number, step: number) {

    const isNonIntegerValue = !Number.isInteger(value);
    const isNonIntegerStepBase = !Number.isInteger(stepBase);
    const isNonIntegerStep = !Number.isInteger(step);

    // Due to limitations in Floating Point Arithmetic (e.g. `0.3 - 0.2 !== 0.1` or
    // `0.5 % 0.1 !== 0`), we need to convert all numbers to integers.
    if (isNonIntegerValue || isNonIntegerStepBase || isNonIntegerStep) {
        const valueDecimals = isNonIntegerValue ? countDecimals(value) : 0;
        const stepBaseDecimals = isNonIntegerStepBase ? countDecimals(stepBase) : 0;
        const stepDecimals = isNonIntegerStep ? countDecimals(step) : 0;

        const decimalCount = Math.max(valueDecimals, stepBaseDecimals, stepDecimals);
        const multiplier = Math.pow(10, decimalCount);

        value = value * multiplier;
        stepBase = stepBase * multiplier;
        step = step * multiplier;

        if (isNonIntegerValue) value = Math.round(value);
        if (isNonIntegerStepBase) stepBase = Math.round(stepBase);
        if (isNonIntegerStep) step = Math.round(step);
    }

    return (value - stepBase) % step === 0;
}

// stolen from angularjs codebase
function countDecimals(num) {
    const numString = num.toString();
    const decimalSymbolIndex = numString.indexOf('.');

    if (decimalSymbolIndex === -1) {
        if (-1 < num && num < 1) {
            // It may be in the exponential notation format (`1e-X`)
            const match = /e-(\d+)$/.exec(numString);

            if (match) {
                return Number(match[1]);
            }
        }

        return 0;
    }

    return numString.length - decimalSymbolIndex - 1;
}